import React, {
  useState,
  useEffect,
  useCallback,
} from "react";
import { useLoading } from "shared/contexts/Loading";
import apiCall from "shared/services/ApiAdapter";
import { useTranslation } from "shared/contexts/I18nContext";
import { useSubscription } from "shared/contexts/Subscription";
import ContentHeaderSection from "../../shared/common/ContentHeaderSection";
import KebabMenu from "shared/common/KebabMenu";
import FeatureLimitDisplay from "shared/common/FeatureLimitDisplay";
import { useMasterList } from "shared/hooks/useMasterList";
import masterApiCall from "shared/services/MasterDataApi";
import { getCurrentUserId } from "shared/utils/SessionDataUtils";
import { ProtectedVisibility } from "shared/security/ProtectedRoute";
import CourseDetailsModal from "./CourseDetailsModal";

const CoursesContent = ({ navigateTo, studentId, useMockData, displayToast, enroll }) => {
  const [courses, setCourses] = useState([]);
  const [selectedCourseForModal, setSelectedCourseForModal] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [useNewJourney, setUseNewJourney] = useState(false);
  const { t } = useTranslation();
  const { startLoading, stopLoading } = useLoading();
  const { limits, canPerformAction } = useSubscription();
  const userId = getCurrentUserId();

  const {
    masterData,
    isLoading: masterDataLoading,
    error: masterDataError
  } = useMasterList({ useMockData, displayToast, startLoading, stopLoading, apiCall: masterApiCall });


  const getLabelByCode = useCallback((category, code) => {
    if (!masterData || !masterData[category]) {
      return '';
    }
    const item = masterData[category].find(item => item.value === code);
    return item ? item.label : '';
  }, [masterData]);

  const getCourses = useCallback(async () => {
    if (masterDataLoading) {
      return;
    }

    try {
      var url = `/courses?isActive.equals=true&createdBy.equals=${userId}`;
      if (enroll) {
        url = `/courses?isActive.equals=true`;
      }
      const data = await apiCall(
        url,
        useMockData,
        "GET",
        null,
        startLoading,
        stopLoading,
        displayToast
      );
      setCourses(data || []);
    } catch (error) {
      console.error("Failed to fetch courses:", error);
    }
  }, [useMockData, startLoading, stopLoading, displayToast, masterDataLoading]);

  useEffect(() => {
    getCourses();
  }, [getCourses]);

  const canCreateCourse = canPerformAction("maxCourses", courses?.length ?? 0);

  const rightSideContentTop = (
    <>
      <div className="flex items-center mr-4">
        <label className="flex items-center cursor-pointer">
          <div className="relative">
            <input
              type="checkbox"
              className="sr-only"
              checked={useNewJourney}
              onChange={() => setUseNewJourney(!useNewJourney)}
            />
            <div className={`block w-10 h-6 rounded-full transition-colors ${useNewJourney ? 'bg-green-500' : 'bg-gray-400'}`}></div>
            <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${useNewJourney ? 'translate-x-4' : ''}`}></div>
          </div>
          <div className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300 hidden sm:block">
            {useNewJourney ? "Modern View" : "Classic View"}
          </div>
        </label>
      </div>
      <div className="relative w-full max-w-40">
        <input
          type="text"
          placeholder={t("search")}
          className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600 text-gray-900"
        />
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
          🔍
        </span>
      </div>
      <div className="flex items-center justify-center w-10 h-10 text-xl text-gray-100 bg-gray-800 rounded-full cursor-pointer dark:bg-gray-600 dark:text-gray-200">
        ⚙️
      </div>
      <ProtectedVisibility requiredRoles={['ROLE_TEACHER']}>
        <button
          onClick={() => navigateTo("createCourse")}
          className={`px-5 py-2 rounded-lg font-medium transition-colors ${canCreateCourse
            ? "bg-gray-800 text-gray-100 hover:bg-gray-700 dark:bg-gray-600 dark:hover:bg-gray-500"
            : "bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-400"
            }`}
          disabled={!canCreateCourse}
        >
          {t("createCourse")}
        </button>
      </ProtectedVisibility>
    </>
  );

  return (
    <div className="p-8 md:px-10">
      <ContentHeaderSection
        title={t("courses")}
        subtitle={t("manageCourses")}
        rightContentTop={rightSideContentTop}
        rightContentBottom={
          <ProtectedVisibility requiredRoles={['ROLE_TEACHER']}>
            <FeatureLimitDisplay
              feature="maxCourses"
              currentCount={courses.length}
              limit={limits.maxCourses}
            />
          </ProtectedVisibility>
        }
      />

      {masterDataLoading ? (
        <div className="text-center text-gray-600 dark:text-gray-400">
          {t("loadingMasterData")}
        </div>
      ) : masterDataError ? (
        <div className="col-span-full text-center text-red-500">
          {t("errorLoadingMasterData")}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {courses.length === 0 ? (
            <div className="col-span-full text-center text-gray-600 dark:text-gray-400">
              {t("noCoursesFound")}
            </div>
          ) : (
            courses.map((course) => (
              CourseCard(course, userId, enroll, studentId, navigateTo, t, getLabelByCode, setSelectedCourseForModal, setIsModalOpen, useNewJourney)
            ))
          )}
        </div>
      )}

      {enroll && (
        <CourseDetailsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          course={selectedCourseForModal}
          studentId={studentId}
          onEnroll={(sid, cid) => navigateTo("createRequestEnrollment", sid, cid)}
          getLabelByCode={getLabelByCode}
        />
      )}
    </div>
  );
};

export default CoursesContent;

function CourseCard(course, userId, enroll, studentId, navigateTo, t, getLabelByCode, setSelectedCourseForModal, setIsModalOpen, useNewJourney) {
  const cardDetails = (
    <>
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 leading-tight uppercase">
          {course.courseName}
        </h3>
        {/* Tutor Name uses standard gray for secondary info */}
        {enroll && (
          <div className="mt-1 text-sm font-medium tracking-wide text-gray-500 dark:text-gray-400">
            Tutor: {course.tutor?.user?.firstName} {course.tutor?.user?.lastName}
          </div>
        )}
      </div>


      {/* --- 2. Body Section (Content & Metadata) --- */}
      <div className="flex flex-grow flex-col">
        {/* Description - Standard text size and color for readability */}
        {course.description && (
          <p className="mb-4 line-clamp-3 text-base text-gray-700 dark:text-gray-300">
            {course.description}
          </p>
        )}

        {/* Metadata - Clear definition list style, pushed down by flex-grow */}
        <div className="mt-auto space-y-2 pt-2 dark:border-gray-700">
          {[
            { label: 'Curriculum', value: getLabelByCode('curricula', course.curriculum) },
            { label: 'Class', value: getLabelByCode('academicClasses', course.academicClass) },
            { label: 'Subject', value: getLabelByCode('subjects', course.subject) },
          ].map((item, index) => (
            <div key={index} className="flex justify-left text-sm">
              <span className="px-2  font-semibold text-gray-500 dark:text-gray-400">{item.label}</span>
              <span className=" font-medium text-gray-800 dark:text-gray-200">{item.value}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="absolute bottom-4 right-4 flex space-x-2">

        {enroll && <button
          onClick={(event) => {
            event.stopPropagation();
            if (useNewJourney) {
              setSelectedCourseForModal(course);
              setIsModalOpen(true);
            } else {
              navigateTo("createRequestEnrollment", studentId, course.id);
            }
          }}
          className="w-full rounded-lg bg-gray-900 py-3 px-3 text-base font-semibold text-white shadow-lg transition-all duration-300 hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 focus:outline-none focus:ring-4 focus:ring-gray-300 dark:focus:ring-gray-600"        >
          {useNewJourney ? t("View Details") : t("Enroll")}
        </button>
        }
      </div>
    </>
  )
  return (userId === course.createdBy) ? (
    <div
      key={course.id}
      onClick={() => {
        if (useNewJourney) {
          navigateTo("courseHub", course.courseName, course.id);
        } else {
          navigateTo("myBatches", course.courseName, course.id);
        }
      }}
      className="relative flex flex-col items-start p-6 
             bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800
             rounded-xl border border-gray-300 dark:border-gray-700
             shadow-sm hover:shadow-md 
             gap-6 cursor-pointer 
             transition-transform duration-300 ease-out hover:scale-[1.01]"

    >
      {/* Action Menu */}
      <div className="absolute top-4 right-4">
        <KebabMenu onEdit={() => navigateTo("updateCourse", course.id)} />
      </div>

      {/* Card Content */}
      {cardDetails}
    </div>

  ) : (
    <div
      key={course.id}
      className="relative flex flex-col items-start p-6 bg-white rounded-xl shadow-lg border-l-2 border-t-2 dark:bg-gray-800 gap-6 transition-shadow duration-200"
    >
      {cardDetails}
    </div>
  );
}
